import { useFetchInvoiceStatus } from "@verde/entities";
import { CheckIcon, ClockIcon, CopyIcon } from "@verde/icons";
import { Alert, IconButton, Input, Link } from "@verde/ui-core";
import { Countdown, toast } from "@verde/utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCreditDialog } from "./credit-dialog";
import CreditBarDialogCreditItem from "./credit-dialog-credit-item";

const REFETCH_STATUS_INTERVAL = 10000;

function CreditDialogPayment() {
  const { selectedInvoice, setKind, selectedCredit } = useCreditDialog();

  const verifyStatus = useFetchInvoiceStatus(selectedInvoice?.id);

  const [copied, setCopied] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopy = useCallback(async () => {
    if (copied === true || !selectedInvoice) return;

    try {
      await navigator.clipboard.writeText(selectedInvoice?.digitable_line);
      setCopied(true);
    } finally {
      toast.success("Código PIX copiado.");

      setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
  }, [copied, selectedInvoice]);

  const handleVerify = useCallback(async () => {
    const { status } = await verifyStatus.mutateAsync();

    if (status === "expired") {
      setKind("expired");
      return;
    }

    if (status === "paid" || status === "externally_paid") {
      setKind("success");
      return;
    }
  }, [verifyStatus, setKind]);

  useEffect(() => {
    const startPolling = () => {
      intervalRef.current = setInterval(() => {
        handleVerify();
      }, REFETCH_STATUS_INTERVAL);
    };

    startPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [handleVerify]);

  if (!selectedInvoice) {
    return <Alert kind="error">Ocorreu um erro, tente novamente.</Alert>;
  }

  return (
    <div className="grid gap-4">
      <CreditBarDialogCreditItem
        prefix="Comprando:"
        title={`${selectedCredit?.credits} ${selectedCredit?.credits === 1 ? "crédito" : "créditos"}`}
        value={selectedCredit?.value ?? 0}
        selected
      />

      <hr />

      <div className="mx-auto h-[200px] w-[200px] rounded bg-neutral-light-1 p-4">
        <img
          alt=""
          src={selectedInvoice?.image_url}
          className="h-full w-full object-contain"
        />
      </div>

      <div className="grid gap-2">
        <p className="text-base font-medium text-neutral-dark-2">
          Escaneie este código QR ou copie abaixo para pagar.
        </p>

        <Input
          readOnly
          defaultValue={selectedInvoice?.digitable_line}
          addon={
            <IconButton
              onClick={handleCopy}
              className={
                copied
                  ? "bg-brand-green-light text-neutral-light-pure"
                  : undefined
              }
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
            </IconButton>
          }
        />
      </div>

      <hr />

      <Alert
        classNames={{
          base: "p-4",
        }}
      >
        <p className="flex items-center gap-1 text-lg font-medium text-neutral-dark-2">
          <ClockIcon className="h-4 w-4" />
          Processando pagamento:
          <Countdown date={selectedInvoice?.due_date} />
        </p>

        <p className="flex items-center gap-1 text-base font-medium text-neutral-dark-2">
          Não saia dessa janela até o pagamento ser processado.{" "}
          <Link disabled={verifyStatus.isLoading} onClick={handleVerify}>
            {verifyStatus.isLoading
              ? "Validando..."
              : "Pagamento já foi feito."}
          </Link>
        </p>
      </Alert>
    </div>
  );
}

export default React.memo(CreditDialogPayment);
